import migratedRoutesData from '../../../../astro-routes.json'
import {removeTrailingSlash} from './remove-trailing-slash'

// Same repo-root manifest the origin-request Lambda reads (isMigratedRoute). Bundled at build
// time - keep it a plain static import so the check is synchronous with no per-render work.
// Every entry is a regex source, anchored here as ^(?:<entry>)$ so an entry can never accidentally
// match substrings. A literal path like "/us/legal" is a valid regex matching only itself, so
// historical literal entries keep exact-match semantics - which matters, because "/us" being
// migrated does not imply "/us/legal" is.
// Entries are compiled raw, exactly as the Lambda compiles them. Stripping a trailing slash here
// would make this reader more permissive than production: the origin handler 301s any trailing-slash
// URI before routing (handleTrailingSlashRedirect), so an entry like "/us/legal/" can never match a
// real request.uri and the page stays on Gatsby. Normalising it here would force a hard navigation
// to an Astro page that is not being served. retired-routes.ts fails the build for the same reason.
// An entry that fails to compile is skipped silently (no logging dependency in the browser bundle)
// so one bad pattern cannot disable all other routes.
const migratedRoutePatterns: RegExp[] = (() => {
    if (!Array.isArray(migratedRoutesData.migrated_routes)) {
        return []
    }

    return migratedRoutesData.migrated_routes.reduce<RegExp[]>((patterns, entry) => {
        if (typeof entry !== 'string') {
            return patterns
        }

        try {
            patterns.push(new RegExp(`^(?:${entry})$`))
        } catch {
            // skip invalid regex entries silently
        }

        return patterns
    }, [])
})()

/**
 * True when `path` has been migrated to Astro, per the checked-in astro-routes.json manifest.
 *
 * Gatsby's client-side <Link> navigation renders its own legacy page from page-data.json and never
 * issues a document request, so the origin-request Lambda can't rewrite it to the Astro build in
 * the unified bucket. LocalizedLink uses this to fall back to a hard navigation for migrated routes
 * (mirroring how it already does so for pages with running experiments), ensuring the request
 * reaches the Lambda and the user sees the Astro version.
 *
 * Query strings and hash fragments are stripped before matching, since link targets legitimately
 * carry them (e.g. "/us/legal?ref=footer", "/us/legal#section") and the manifest keys on pathname.
 * Each manifest entry is matched as an anchored regex — e.g. "/(pt|ee|it)/legal" covers several
 * locales with one entry, mirroring the semantics in the origin-request Lambda.
 */
export const isMigratedRoute = (path: string): boolean => {
    if (typeof path !== 'string') return false
    const pathname = removeTrailingSlash(path.split(/[?#]/)[0])
    return migratedRoutePatterns.some((pattern) => pattern.test(pathname))
}
